import type { ReactNode } from "react";
import { TextLink } from "./TextLink";

/**
 * Slack's own markdown ("mrkdwn"), drawn the way Slack draws it, so the person approves a message as it will appear:
 * *bold*, _italic_, ~strike~, `code`, ``` blocks ```, > quotes, <https://…|links>, <@U…> and <#C…|channel> mentions.
 * Everything else is the text itself; nothing is ever read as HTML.
 */

const INLINE = /(`[^`\n]+`)|(<[^<>\n]+>)|(\*[^*\n]+\*)|(_[^_\n]+_)|(~[^~\n]+~)/g;

const BROADCAST = new Set(["channel", "here", "everyone"]);

/**
 * A link Slack would make: an address, with its words or itself. Only web and mail addresses are ever a link here. A
 * mention is drawn as what Slack does with it, never by its label — a label can say "ok" on an @channel that notifies
 * everyone in it, or a friendly name on somebody else's id.
 */
function angle(inner: string, key: number): ReactNode {
  const [target = "", label] = inner.split("|", 2);
  if (target.startsWith("@")) return <span key={key} className="rounded bg-info/10 px-0.5 font-mono text-info">@{target.slice(1)}</span>;
  if (target.startsWith("#")) return <span key={key} className="rounded bg-info/10 px-0.5 text-info">#{label ?? target.slice(1)}</span>;
  if (target.startsWith("!")) {
    const [special = "", id] = target.slice(1).split("^");
    if (BROADCAST.has(special)) return <span key={key} className="rounded bg-warning/10 px-0.5 font-medium text-warning">@{special}</span>;
    if (special === "subteam") return <span key={key} className="rounded bg-warning/10 px-0.5 font-mono text-warning">@group {id}</span>;
    return <span key={key}>{label ?? special}</span>;
  }
  if (/^(https?:\/\/|mailto:)/i.test(target)) return <TextLink key={key} href={target}>{label ?? target.replace(/^mailto:/i, "")}</TextLink>;
  return `<${inner}>`;
}

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(INLINE)) {
    if (m.index! > last) out.push(text.slice(last, m.index));
    const [token] = m;
    const inner = token.slice(1, -1);
    if (m[1]) out.push(<code key={key++} className="rounded bg-fill px-1 font-mono text-xs text-danger">{inner}</code>);
    else if (m[2]) out.push(angle(inner, key++));
    else if (m[3]) out.push(<strong key={key++} className="font-semibold">{inline(inner)}</strong>);
    else if (m[4]) out.push(<em key={key++}>{inline(inner)}</em>);
    else out.push(<s key={key++}>{inline(inner)}</s>);
    last = m.index! + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function SlackMarkdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  const parts = text.split(/```/);
  parts.forEach((raw, i) => {
    // Every other part sits between ``` fences: a code block, kept exactly.
    if (i % 2 === 1) {
      blocks.push(<pre key={`c${i}`} className="my-1 overflow-x-auto rounded-lg border border-line bg-fill-subtle p-2 font-mono text-xs whitespace-pre-wrap">{raw.replace(/^\n|\n$/g, "")}</pre>);
      return;
    }
    // The line break beside a fence belongs to the fence, not to a blank line of its own.
    const part = raw.replace(i > 0 ? /^\n/ : /(?!)/, "").replace(i < parts.length - 1 ? /\n$/ : /(?!)/, "");
    if (!part && parts.length > 1) return;
    part.split("\n").forEach((row, j) => {
      const quote = /^>\s?/.exec(row);
      const body = quote ? row.slice(quote[0].length) : row;
      const shown = body ? inline(body) : "\u00a0";
      blocks.push(
        quote ? (
          <div key={`${i}-${j}`} className="border-l-4 border-line pl-2 text-muted">{shown}</div>
        ) : (
          <div key={`${i}-${j}`}>{shown}</div>
        ),
      );
    });
  });
  return <div className="text-reading leading-6 break-words">{blocks}</div>;
}
